import React, { memo } from 'react';

import { areEqual } from 'Utils/equalityChecks';

import { CompanyGroup } from '../types';

import classes from './referralDetailTable.module.css';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateString: string): string => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

interface ReferralDetailTableProps {
  group: CompanyGroup;
  onClose?: () => void;
}

function ReferralDetailTableComponent({ group, onClose }: ReferralDetailTableProps) {
  const sortedOpportunities = [...group.opportunities].sort((a, b) => b.value - a.value);

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <h5 className={classes.title}>
          {group.company} — {formatCurrency(group.value)} ({group.count}{' '}
          {group.count === 1 ? 'opportunity' : 'opportunities'})
        </h5>
        {onClose && (
          <button
            type="button"
            className={`btn-close ${classes.closeButton}`}
            aria-label="Close"
            onClick={onClose}
          />
        )}
      </div>

      <div className={classes.tableWrapper}>
        <table className={`table table-hover ${classes.table}`}>
          <thead>
            <tr>
              <th>Opportunity</th>
              <th>Value</th>
              <th>Lead Contact</th>
              <th>Referring Contact</th>
              <th>Created Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedOpportunities.map((opp) => (
              <tr key={opp.id}>
                <td>{opp.opportunityName}</td>
                <td>{formatCurrency(opp.value)}</td>
                <td>
                  <div>{opp.leadContactName}</div>
                  <small className={classes.secondaryText}>
                    {opp.leadEmail || opp.leadPhone || '—'}
                  </small>
                </td>
                <td>
                  <div>{opp.referringContactName}</div>
                  {opp.referringCompany &&
                    opp.referringCompany !== opp.referringContactName &&
                    opp.referringCompany !== 'Unattributed' && (
                      <small className={classes.secondaryText}>{opp.referringCompany}</small>
                    )}
                </td>
                <td>{formatDate(opp.createdAt)}</td>
                <td>
                  <span className={`badge ${classes.statusBadge} ${classes[`status${opp.status}`]}`}>
                    {opp.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const ReferralDetailTableMemo = memo(ReferralDetailTableComponent, areEqual);

export { ReferralDetailTableMemo as ReferralDetailTable };
